import { AppAdminFormLabel, AppButton, AppForm, AppSelect, AppTextField } from '@/components/atoms';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import {
  createClinicUserFormSchema,
  createClinicUserEditFormSchema,
  type ClinicUserFormData,
  type ClinicUserEditFormData,
} from '@/schemas/clinicUserSchema';
import { useClinicOptionsQuery } from '@/hooks/admin/useClinicOptionsQuery';
import { useClinicAdminQuery } from '@/hooks/admin/useClinicAdminQuery';
import {
  CLINIC_USER_ADMIN_EXISTS_MESSAGE,
  CLINIC_USER_ADMIN_EMAIL_SSO_MESSAGE,
  CLINIC_USER_ROLE_ADMIN,
  CLINIC_USER_ROLE_NORMAL,
  CLINIC_USER_ROLE_OPTIONS,
} from '@/constants/clinicUser';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { Col, Row } from 'antd';
import React, { useState } from 'react';
import { Controller, useWatch, type SubmitHandler } from 'react-hook-form';
import { isClinicAdminSsoMappingEnabled } from '@/features/sso/clinicUserSsoPolicy';

type FormData = ClinicUserFormData | ClinicUserEditFormData;
type ApiFieldErrors = Partial<Record<keyof FormData, string>>;

export interface ClinicUserInitialData {
  id: number;
  clinic_id: number;
  /** 権限: 一般権限 (0) / 管理者権限 (1) */
  role: number;
  login_id: string;
  password: string | null;
  name: string;
  email: string | null;
  available_from: string | null;
  available_to: string | null;
}

interface CreateClinicUserFormProps {
  siteType: string;
  clinicId?: number;
  /** When true, clinic is fixed (from URL or parent screen) and cannot be changed. */
  isClinicLocked?: boolean;
  /** Display name for fixed clinic (latest information name or clinic_number). */
  lockedClinicDisplayName?: string | null;
  /** Tenant code from the fixed clinic response; differs from a super-admin route. */
  clinicSiteType?: string | null;
  onSubmit?: (data: FormData) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  apiErrors?: ApiFieldErrors;
  /** Initial data for edit mode */
  initialData?: ClinicUserInitialData | null;
  /** Whether this is edit mode */
  isEditMode?: boolean;
  /** When true (e.g. after clinic_id API error), set clinic to first option and disable selector */
  resetClinicToFirstAndDisable?: boolean;
  /** Whether the page was opened with a clinic_id query parameter (even if invalid/soft-deleted). */
  hasClinicIdParam?: boolean;
  /** Control whether clinic options query is enabled (e.g. wait for clinic_id check first). */
  clinicOptionsEnabled?: boolean;
  /** While true, do not render/select clinic options yet. */
  waitForClinicCheck?: boolean;
}

const CreateClinicUserForm: React.FC<CreateClinicUserFormProps> = ({
  siteType,
  clinicId,
  isClinicLocked = false,
  lockedClinicDisplayName = null,
  clinicSiteType = null,
  onSubmit,
  onCancel,
  isLoading = false,
  apiErrors = {},
  initialData = null,
  isEditMode = false,
  resetClinicToFirstAndDisable = false,
  hasClinicIdParam = false,
  clinicOptionsEnabled = true,
  waitForClinicCheck = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [forcedClinicId, setForcedClinicId] = useState<number | undefined>(undefined);
  const {
    data: clinicOptions,
    isLoading: isLoadingOptions,
    refetch: refetchClinicOptions,
  } = useClinicOptionsQuery(siteType, { enabled: clinicOptionsEnabled });
  const effectiveClinicOptions = waitForClinicCheck ? undefined : clinicOptions;

  const selectedClinicUsesSsoMapping = (selectedClinicId: unknown): boolean =>
    isClinicAdminSsoMappingEnabled({
      routeSiteType: siteType,
      clinicId: selectedClinicId,
      clinicOptions: effectiveClinicOptions,
      clinicSiteType,
    });

  // Resolve the requirement from the submitted clinic id. This keeps validation
  // correct when a super admin changes the dropdown immediately before submit.
  const schema = isEditMode
    ? createClinicUserEditFormSchema(value => selectedClinicUsesSsoMapping(value.clinic_id))
    : createClinicUserFormSchema(value => selectedClinicUsesSsoMapping(value.clinic_id));

  const { control, handleSubmit, setValue, getValues } = useTrimmedForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      clinic_id:
        initialData?.clinic_id ??
        clinicId ??
        (!hasClinicIdParam && effectiveClinicOptions && effectiveClinicOptions.length > 0
          ? effectiveClinicOptions[0].id
          : undefined),
      role: initialData?.role ?? CLINIC_USER_ROLE_NORMAL,
      login_id: initialData?.login_id ?? '',
      password: initialData?.password ?? '',
      name: initialData?.name ?? '',
      email: initialData?.email ?? '',
      available_from: initialData?.available_from ?? null,
      available_to: initialData?.available_to ?? null,
    },
  });

  // 権限: a clinic holds at most one 管理者権限 user, so that option is closed off once
  // another user of the selected clinic already holds it.
  const selectedClinicId = useWatch({ control, name: 'clinic_id' });
  const ssoMappingEnabled = selectedClinicUsesSsoMapping(selectedClinicId);
  // Follows the dropdown live: the note has to appear the moment 管理者権限 is picked, not
  // after a save tells the operator the field was required all along.
  const selectedRole = useWatch({ control, name: 'role' });
  const { data: clinicAdminData } = useClinicAdminQuery(siteType, selectedClinicId);
  const existingAdminId = clinicAdminData?.data?.admin_clinic_user_id ?? null;
  const isAdminRoleTaken = existingAdminId !== null && existingAdminId !== initialData?.id;
  const roleOptions = CLINIC_USER_ROLE_OPTIONS.map(option => ({
    ...option,
    disabled: option.value === CLINIC_USER_ROLE_ADMIN && isAdminRoleTaken,
  }));

  // Switching clinics can leave 管理者権限 selected for a clinic that already has one.
  // Fall back to 一般権限 so the form never submits a value the dropdown now refuses.
  React.useEffect(() => {
    if (isAdminRoleTaken && getValues('role') === CLINIC_USER_ROLE_ADMIN) {
      setValue('role', CLINIC_USER_ROLE_NORMAL, { shouldValidate: false, shouldDirty: true });
    }
  }, [isAdminRoleTaken, getValues, setValue]);

  // Effect to set default clinic_id if not provided and options are loaded
  React.useEffect(() => {
    if (
      !initialData?.clinic_id &&
      !clinicId &&
      !hasClinicIdParam &&
      !waitForClinicCheck &&
      effectiveClinicOptions &&
      effectiveClinicOptions.length > 0
    ) {
      const currentVal = control._formValues.clinic_id;
      if (!currentVal) {
        control._reset({
          ...control._formValues,
          clinic_id: effectiveClinicOptions[0].id,
        });
      }
    }
  }, [
    effectiveClinicOptions,
    clinicId,
    hasClinicIdParam,
    initialData,
    control,
    waitForClinicCheck,
  ]);

  // When API returned clinic_id error: refetch options, then force clinic to latest first option.
  // This avoids using stale cached options where the deleted clinic may still appear.
  React.useEffect(() => {
    if (!resetClinicToFirstAndDisable) {
      setForcedClinicId(undefined);
      return;
    }
    if (waitForClinicCheck) return;

    // First, immediately force to current first option for instant UI feedback.
    const currentFirstOptionId =
      effectiveClinicOptions && effectiveClinicOptions.length > 0
        ? effectiveClinicOptions[0].id
        : undefined;
    if (currentFirstOptionId !== undefined) {
      setForcedClinicId(currentFirstOptionId);
      setValue('clinic_id', currentFirstOptionId, { shouldValidate: false, shouldDirty: true });
    }

    let cancelled = false;
    const forceFirstClinic = async () => {
      const latest = await refetchClinicOptions();
      if (cancelled) return;

      const latestOptions = latest.data ?? effectiveClinicOptions ?? [];
      const firstOptionId = latestOptions.length > 0 ? latestOptions[0].id : undefined;
      if (firstOptionId !== undefined) {
        setForcedClinicId(firstOptionId);
        setValue('clinic_id', firstOptionId, { shouldValidate: false, shouldDirty: true });
      }
    };

    void forceFirstClinic();
    return () => {
      cancelled = true;
    };
  }, [
    resetClinicToFirstAndDisable,
    refetchClinicOptions,
    effectiveClinicOptions,
    setValue,
    waitForClinicCheck,
  ]);

  const handleValidSubmit: SubmitHandler<FormData> = async data => {
    if (!onSubmit) return;
    const payload: FormData = {
      ...data,
      email: data.email && data.email.trim() !== '' ? data.email.trim() : '',
      available_from:
        data.available_from && data.available_from.trim() !== '' ? data.available_from : null,
      available_to: data.available_to && data.available_to.trim() !== '' ? data.available_to : null,
    };
    await onSubmit(payload);
  };

  return (
    <form
      onSubmit={handleSubmit(handleValidSubmit)}
      className="space-y-4"
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <AppAdminFormLabel label="治療院名" required />
          <Controller
            name="clinic_id"
            control={control}
            render={({ field, fieldState }) => {
              const errorMessage = fieldState.error?.message || apiErrors.clinic_id;
              const hasError = !!errorMessage;
              const isLocked = isClinicLocked || isEditMode;
              const isForcedValue = resetClinicToFirstAndDisable && forcedClinicId !== undefined;
              const fixedId = clinicId ?? field.value;
              const optionLabel =
                lockedClinicDisplayName ??
                effectiveClinicOptions?.find(opt => opt.id === fixedId)?.name ??
                effectiveClinicOptions?.find(opt => opt.id === fixedId)?.clinic_number ??
                (fixedId ? `ID: ${fixedId}` : '');

              return (
                <AppForm.Item
                  validateStatus={hasError ? 'error' : undefined}
                  help={errorMessage}
                  className="mb-0"
                >
                  {isLocked ? (
                    <>
                      {fixedId && field.value !== fixedId && field.onChange(fixedId)}
                      <AppSelect
                        value={fixedId}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled
                        loading={isLoadingOptions || waitForClinicCheck}
                        options={
                          effectiveClinicOptions && effectiveClinicOptions.length > 0
                            ? effectiveClinicOptions.map(opt => ({
                                label: opt.name,
                                value: opt.id,
                              }))
                            : fixedId
                              ? [
                                  {
                                    label: optionLabel,
                                    value: fixedId,
                                  },
                                ]
                              : []
                        }
                        status={hasError ? 'error' : undefined}
                        className="w-full"
                      />
                    </>
                  ) : (
                    <AppSelect
                      {...field}
                      value={isForcedValue ? forcedClinicId : field.value}
                      onChange={val => {
                        const id = val === undefined || val === null ? undefined : Number(val);
                        field.onChange(id);
                        setForcedClinicId(undefined);
                      }}
                      loading={isLoadingOptions || waitForClinicCheck}
                      options={effectiveClinicOptions?.map(opt => ({
                        label: opt.name,
                        value: opt.id,
                      }))}
                      status={hasError ? 'error' : undefined}
                      className="w-full"
                    />
                  )}
                </AppForm.Item>
              );
            }}
          />
        </Col>
        <Col xs={24} md={12}>
          <AppAdminFormLabel label="権限" required />
          <Controller
            name="role"
            control={control}
            render={({ field, fieldState }) => {
              const errorMessage = fieldState.error?.message || apiErrors.role;
              const hasError = !!errorMessage;
              return (
                <>
                  <AppForm.Item
                    validateStatus={hasError ? 'error' : undefined}
                    help={errorMessage}
                    className="mb-0"
                  >
                    <AppSelect
                      {...field}
                      options={roleOptions}
                      status={hasError ? 'error' : undefined}
                      className="w-full"
                    />
                  </AppForm.Item>
                  {isAdminRoleTaken && (
                    <p className="mt-1 text-xs text-gray-500">{CLINIC_USER_ADMIN_EXISTS_MESSAGE}</p>
                  )}
                </>
              );
            }}
          />
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <AppAdminFormLabel label="ユーザーID" required />
          <Controller
            name="login_id"
            control={control}
            render={({ field, fieldState }) => {
              const errorMessage = fieldState.error?.message || apiErrors.login_id;
              const hasError = !!errorMessage;
              return (
                <AppForm.Item
                  validateStatus={hasError ? 'error' : undefined}
                  help={errorMessage}
                  className="mb-0"
                >
                  <AppTextField {...field} />
                </AppForm.Item>
              );
            }}
          />
        </Col>
        <Col xs={24} md={12}>
          <div className="flex items-center gap-2 mb-2">
            <AppAdminFormLabel label="パスワード" required className="mb-0" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-blue-200 hover:bg-blue-300"
              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
            >
              {showPassword ? '非表示' : '表示'}
            </button>
          </div>
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => {
              const errorMessage = fieldState.error?.message || apiErrors.password;
              const hasError = !!errorMessage;
              return (
                <AppForm.Item
                  validateStatus={hasError ? 'error' : undefined}
                  help={errorMessage}
                  className="mb-0"
                >
                  <AppTextField
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    status={hasError ? 'error' : undefined}
                    autoComplete="new-password"
                  />
                </AppForm.Item>
              );
            }}
          />
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <AppAdminFormLabel label="名前" required />
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => {
              const errorMessage = fieldState.error?.message || apiErrors.name;
              const hasError = !!errorMessage;
              return (
                <AppForm.Item
                  validateStatus={hasError ? 'error' : undefined}
                  help={errorMessage}
                  className="mb-0"
                >
                  <AppTextField {...field} />
                </AppForm.Item>
              );
            }}
          />
        </Col>
        <Col xs={24} md={12}>
          {/* 必須 only for 管理者権限: that role's address becomes the Cognito username the SSO
              link is keyed on, and the backend requires it for that role alone. */}
          <AppAdminFormLabel
            label="メールアドレス"
            required={ssoMappingEnabled && selectedRole === CLINIC_USER_ROLE_ADMIN}
          />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => {
              const errorMessage = fieldState.error?.message || apiErrors.email;
              const hasError = !!errorMessage;
              return (
                <AppForm.Item
                  validateStatus={hasError ? 'error' : undefined}
                  help={errorMessage}
                  className="mb-0"
                >
                  <AppTextField {...field} type="text" />
                </AppForm.Item>
              );
            }}
          />
          {ssoMappingEnabled && selectedRole === CLINIC_USER_ROLE_ADMIN && (
            <p
              data-testid="clinic-user-email-sso-help"
              className="mt-1 text-xs text-gray-500"
            >
              {CLINIC_USER_ADMIN_EMAIL_SSO_MESSAGE}
            </p>
          )}
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24}>
          <AppAdminFormLabel label="有効期限" required />
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Controller
                name="available_from"
                control={control}
                render={({ field, fieldState }) => {
                  const errorMessage = fieldState.error?.message || apiErrors.available_from;
                  const hasError = !!errorMessage;
                  return (
                    <AppForm.Item
                      validateStatus={hasError ? 'error' : undefined}
                      help={errorMessage}
                      className="mb-0"
                    >
                      <RA2NDatePicker
                        {...field}
                        placeholder=""
                        error={hasError}
                        className="w-full"
                      />
                    </AppForm.Item>
                  );
                }}
              />
            </div>
            <span className="text-gray-700">から</span>
            <div className="flex-1">
              <Controller
                name="available_to"
                control={control}
                render={({ field, fieldState }) => {
                  const errorMessage = fieldState.error?.message || apiErrors.available_to;
                  const hasError = !!errorMessage;
                  return (
                    <AppForm.Item
                      validateStatus={hasError ? 'error' : undefined}
                      help={errorMessage}
                      className="mb-0"
                    >
                      <RA2NDatePicker
                        {...field}
                        placeholder=""
                        error={hasError}
                        className="w-full"
                      />
                    </AppForm.Item>
                  );
                }}
              />
            </div>
          </div>
        </Col>
      </Row>

      <div className="flex gap-2 pt-4">
        <AppButton type="primary" htmlType="submit" loading={isLoading}>
          更新
        </AppButton>
        {onCancel && (
          <AppButton type="default" onClick={onCancel}>
            詳細画面へ戻る
          </AppButton>
        )}
      </div>
    </form>
  );
};

export default CreateClinicUserForm;
